import { User } from "src/common/models/user"
import { CustomerError } from "../state/types"
import { Customer } from "src/common/models/customer"
import { Alert, Form, Input, Row, Select } from "antd"
import { useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { useEffect } from "react"
import { SystemDescriptions } from "src/common/descriptions"
import { FormActionButtons } from "src/components/FormActionButtons"
import { Col16, Col8 } from "src/components/Columns"
import { getInputRequiredRule } from "src/common/util"
import Search from "antd/es/input/Search"

interface ReduxProps {
    error?: CustomerError
    user?: User
}

interface Props {
    initialValues?: Customer
    onFinish: (customer: Partial<Customer>) => void
    onCancel: () => void
    readOnly?: boolean
}

export const CustomerForm = (props: Props) => {

    const [form] = Form.useForm();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.customer.error,
        user: state.security.user,
    }))

    useEffect(() => {
        if (!props.initialValues) {
            return
        }

        form.setFieldsValue({
            name: props.initialValues.name,
            phone: props.initialValues.phone,
            invoiceReceiverId: props.initialValues.nit,
            manager: props.initialValues.manager,
            address: props.initialValues.address,
            status: props.initialValues.isActive
        })

        const fields = form.getFieldsValue(true);

        Object.keys(fields).forEach(field => {
            form.setFields([{
                name: field,
                touched: true,
            }]);
        });
    }, [props.initialValues])

    const descriptions = SystemDescriptions.PAGES.CUSTOMER.CREATE
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const onFinish = (formValues) => {
        const customer: Customer = {
            name: formValues.name,
            address: formValues.address,
            nit: formValues.invoiceReceiverId.trim(),
            phone: formValues.phone.replace("-", "").trim(),
            isActive: 1,
            userId: reduxProps.user?.id ?? 0,
            createdAt: "",
            updatedAt: "",
            manager: formValues.manager
        }

        if (props.initialValues && props.initialValues.id) {
            customer.id = props.initialValues.id
            customer.isActive = formValues.status
        }

        props.onFinish(customer)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    const handleSearchInvoiceId = (value) => {
        form.setFields([
            {
                name: "invoiceReceiverId",
                errors: [],
                touched: true,
                value: "CF",
            }
        ])
    }

    const normalizePhoneInput = (value: string = '') => value.replace(/\D/g, '')

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.NAME.LABEL}
                        name={"name"}
                        rules={[
                            getInputRequiredRule("name"),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                            readOnly={props.readOnly}
                            maxLength={50}
                            showCount
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.PHONE.LABEL}
                        name={"phone"}
                        validateTrigger="onBlur"
                        normalize={normalizePhoneInput}
                        rules={[
                            getInputRequiredRule("phone"),
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.PHONE.PLACEHOLDER}
                            readOnly={props.readOnly}
                            maxLength={8}
                            showCount
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.INVOICE_RECEIVER_ID.LABEL}
                        name={"invoiceReceiverId"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("invoiceReceiverId"),
                        ]}
                    >
                        <Search
                            placeholder={descriptions.FORM.INVOICE_RECEIVER_ID.PLACEHOLDER}
                            onSearch={handleSearchInvoiceId}
                            style={{ width: '100%' }}
                            autoComplete="none"
                            readOnly={props.readOnly}
                            maxLength={10}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.MANAGER.LABEL}
                        name={"manager"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("manager"),
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.MANAGER.PLACEHOLDER}
                            readOnly={props.readOnly}
                            maxLength={45}
                            showCount
                        />
                    </Form.Item>
                </Col8>
                <Col16>
                    <Form.Item
                        label={descriptions.FORM.ADDRESS.LABEL}
                        name={"address"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("address"),
                        ]}
                    >
                        <Input
                            placeholder={descriptions.FORM.ADDRESS.PLACEHOLDER}
                            readOnly={props.readOnly}
                            maxLength={45}
                            showCount
                        />
                    </Form.Item>
                </Col16>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.STATUS.LABEL}
                        name={"status"}
                        validateTrigger="onBlur"
                        rules={[
                            getInputRequiredRule("status"),
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    value: 1,
                                    label: commonDescriptions.ACTIVE_STATUS
                                },
                                {
                                    value: 0,
                                    label: commonDescriptions.INACTIVE_STATUS,
                                },
                            ]}
                            placeholder={descriptions.FORM.STATUS.PLACEHOLDER}
                            disabled={props.readOnly}
                        />
                    </Form.Item>
                </Col8>
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {!props.readOnly && renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
